import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const DEFAULT_SEARCH_DELAY_MS = 300;

export interface SearchEmitter {
  readonly remote: boolean;
  readonly pending: boolean;
  readonly schedule: (text: string) => void;
  readonly reset: () => void;
}

export const useSearchEmitter = (onSearch: ((term: string) => void) | undefined, delayMs: number): SearchEmitter => {
  const latest = useRef(onSearch);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSent = useRef('');
  const [pending, setPending] = useState(false);

  useLayoutEffect(() => {
    latest.current = onSearch;
  });

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const send = useCallback((term: string) => {
    if (term === lastSent.current) return;
    lastSent.current = term;
    latest.current?.(term);
  }, []);

  const schedule = useCallback(
    (text: string) => {
      if (latest.current === undefined) return;
      const term = text.trim();
      cancel();
      if (term === lastSent.current) {
        setPending(false);
        return;
      }
      setPending(true);
      timer.current = setTimeout(() => {
        timer.current = null;
        setPending(false);
        send(term);
      }, delayMs);
    },
    [cancel, delayMs, send],
  );

  const reset = useCallback(() => {
    cancel();
    setPending(false);
    send('');
  }, [cancel, send]);

  return { remote: onSearch !== undefined, pending, schedule, reset };
};
